import { useEffect, useState } from 'react'
import { formatMoney } from '@/lib/money'
import { useApi } from '@/lib/ConfigProvider'
import { fetchTransactions } from '@/activity/transactions'
import type { TransactionsState } from '@/activity/transactions'
import { SendPanel } from '@/components/SendPanel'
import './Payments.css'

/**
 * Send money — the design's own surface, at page scale (#86).
 *
 * # Where this comes from
 *
 * Not invented. `docs/design/Tesora - Desktop.html` specifies a Send money
 * panel in full — the two legs, the fee-and-rate line, the arrival note and the
 * Continue button — and Overview already renders it in the right rail. This is
 * that same panel given the room a dedicated screen has, with the design's own
 * tokens and no new component vocabulary.
 *
 * The design document contains ONE screen (§6: Overview at 1440 x 900). So
 * everything here is either transcribed from it or reuses a pattern it
 * established; nothing about the visual language is a guess. The exploration
 * kit that carries the other screens is `#1`, is explicitly not final, and is
 * not readable from here.
 *
 * # It renders fixtures, like Overview, and for the same reason
 *
 * There is no quote endpoint. The backend's customer surface is registrations,
 * onboarding, verification sessions, accounts and cash-outs (§9) — nothing
 * prices a transfer. The figures below are the design's examples.
 *
 * # What it deliberately does not do
 *
 * **No arithmetic.** The fee and the rate are rendered as given; nothing here
 * multiplies an amount by a rate to check it, because amount arithmetic lives
 * in `money.ts` and a second implementation of it on a screen is how a ledger
 * acquires a rounding error.
 *
 * **Continue does nothing yet, and says so.** A button that looks live and is
 * not is worse than one that admits it: `POST /v1/cash-outs` exists now
 * (`go-wallet#390`) but a cash-out is a money instruction that needs its own
 * issue, a strong-authentication challenge bound to the exact amount and
 * destination, and an idempotency key that is one intent (invariant 5).
 */
export function Payments() {
  const api = useApi()
  /*
    Both halves of this screen read the API now.

    The Send panel quotes a real fee against a real currency (#159), and this
    list is the customer's own history — the backend's own description of
    `GET /v1/transactions` names it: "the web app's 'recent activity' and
    'recent payments' were a fixtures file rendered to real customers as their
    own history" (go-wallet#973, #140).
  */
  const [recent, setRecent] = useState<TransactionsState>({ status: 'loading' })
  useEffect(() => {
    let live = true
    void fetchTransactions(api, { limit: 5 }).then((t) => {
      if (live) setRecent(t)
    })
    return () => {
      live = false
    }
  }, [api])

  return (
    <section className="pay">
      <header className="pay__head">
        <h1 className="pay__title">Send money</h1>
        <p className="pay__lede">
          Move money to someone in their own currency. The rate and the fee are shown before you
          commit to anything.
        </p>
      </header>

      <div className="pay__grid">
        <SendPanel />

        <section className="pay__recent" aria-labelledby="pay-recent">
          <h2 id="pay-recent" className="pay__sectionTitle">
            Recent payments
          </h2>
          <ul className="pay__list">
            {recent.status === 'ready' ? (
              recent.transactions.map((a) => (
              <li key={a.ref} className="pay__row">
                <div className="pay__who">
                  {/* Absent, never blank: the contract omits `counterparty`
                      rather than sending an empty string, which would read as
                      a counterparty called nothing. */}
                  <span className="pay__name">{a.counterparty ?? 'Not named'}</span>
                  <span className="pay__kind">
                    {/* `message.display` gates the text (FR-22.3) — the rail
                        cannot tell a payout reference from a stranger's 140
                        characters, so raw text is withheld and `notice` speaks
                        instead. `state` is what was recorded, never inferred
                        from the date. */}
                    {a.message?.text ?? a.message?.notice ?? (a.state === 'settled' ? '' : a.state)}
                  </span>
                </div>
                {/*
                  Signed and carrying its currency, both from money.ts. The
                  sign is explicit rather than a colour, because a customer who
                  cannot distinguish the two colours must still be able to tell
                  money arriving from money leaving; formatMoney emits U+2212
                  for negatives, as the design renders it.
                */}
                <span className={`pay__amount tnum ${a.direction === 'in' ? 'pay__amount--in' : ''}`}>
                  {formatMoney(a.amount, { withCurrency: true, signed: a.direction === 'in' })}
                </span>
              </li>
              ))
            ) : (
              <li className="pay__row pay__row--empty">
                {recent.status === 'loading'
                  ? 'Reading your payments…'
                  : recent.status === 'none'
                    ? 'You have not sent anything yet.'
                    : // "Could not read" and "nothing there" are different
                      // answers, and collapsing them tells somebody their
                      // history is empty when we simply could not look.
                      `We could not read your payments. ${recent.reason}`}
              </li>
            )}
          </ul>
        </section>
      </div>
    </section>
  )
}
